import type {
  IncentivesPayload,
  IncentivesRecord,
  IncentivesState
} from "@dealer-center/ddc-pages-incentives-manager-interface";
import type { IncentiveContentDefaultsPayload } from "@dealer-center/ddc-pages-incentives-manager-lambda";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { Filters } from "./navFilterUtils";
import { defaultFilters } from "../providers/FiltersProvider";

// Incentives Manager takes dates (epoch time) from the API and converts them to local time
// Ref: https://ghe.coxautoinc.com/DDC-Dont-Panic/control-center-ui/blob/74b196cdcfa0dde3d5ec05c065a9df346f401356/plugins/globalvehiclemanager/htdocs/globalvehiclemanager/js/ext-global-vehicle-utils.js#L173
//
export function incentiveDate(epochtime: number) {
  const date = new Date(epochtime);
  const tzOffset = date.getTimezoneOffset();
  const localDateEpochtime = epochtime + tzOffset * 60 * 1000;
  return localDateEpochtime;
}

export function newDateTimeOffset(time: string) {
  const date = new Date(time);
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return date.getTime() - offsetMs;
}

export function capitalizeString(str: string) {
  return str?.length ? str.charAt(0).toUpperCase() + str.slice(1) : str;
}

export const splitByExpiration = (
  ids: string[],
  incentives: IncentivesRecord[]
): { expired: string[]; notExpired: string[] } => {
  const expired: string[] = [];
  const notExpired: string[] = [];
  ids.forEach(id => {
    const record = incentives.find(
      incentive => incentive.content.incentiveId === id
    );
    if (record && record.expirationDate) {
      const displayExpirationDate = new Date(
        incentiveDate(record.expirationDate)
      );
      displayExpirationDate.setHours(23, 59, 59, 999);
      if (displayExpirationDate.getTime() < Date.now()) {
        expired.push(id);
      } else {
        notExpired.push(id);
      }
    }
  });
  return { expired, notExpired };
};

export const getSelectedIds = (
  selections: number[],
  incentives?: IncentivesRecord[]
): string[] => {
  if (!incentives) return [] as string[];
  return selections.map(selectionIndex => {
    const id = incentives[selectionIndex]?.content?.incentiveId;
    return id || "";
  });
};

export function createMockIncentive(
  trimList: string[],
  idx = 0
): IncentivesRecord {
  return {
    globalVehicleTrimIdList: trimList,
    created: Date.now(),
    accountMap: {
      status: "active",
      dealerStackOption: false,
      distributionListIdList: []
    },
    monthlyPayment: 0,
    mileageAllowance: 0,
    mileOverageCharge: 0,
    creatorAccountName: "testAccount",
    creatorAccountId: `testAccountId${idx}`,
    applicableCondition: "new",
    applicableMake: "testMake",
    applicableVehicleList: [],
    type: "cash",
    conditional: false,
    featured: false,
    effectiveDate: Date.now(),
    expirationDate: Date.now() + 100000,
    cashOption: 1000,
    twentyFourMonthApr: 0,
    thirtySixMonthApr: 0,
    thirtyNineMonthApr: 0,
    fortyTwoMonthApr: 0,
    fortyEightMonthApr: 0,
    sixtyMonthApr: 0,
    seventyTwoMonthApr: 0,
    customTermMonths: 0,
    customApr: 0,
    msrp: 0,
    cashDue: 0,
    purchaseOptionPrice: 0,
    vinList: [],
    applicableYear: 2025,
    content: {
      applicableBodyStyle: "testBodyStyle",
      applicableModel: "testModel",
      content: "testContent",
      disclaimer: "testDisclaimer",
      incentiveId: "testIncentiveId",
      languageCode: "en",
      offerDetails: "testOfferDetails",
      shortTitle: "testShortTitle",
      tcdHeadline: "testTcdHeadline",
      tcdKeywords: "testTcdKeywords",
      tcdLine1: "testTcdLine1",
      tcdLine2: "testTcdLine2",
      title: "testTitle"
    }
  };
}

export function hasObjectChanged(current, next): boolean {
  const hasChanged = (obj1, obj2) => {
    if ((!obj1 || !obj2) && obj1 !== obj2) {
      return true;
    }
    if (obj1 && obj2) {
      if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) {
          return true;
        }
        return obj1.some((item, index) => hasChanged(item, obj2[index]));
      } else if (typeof obj1 === "object" && typeof obj2 === "object") {
        const obj1Keys = Object.keys(obj1);
        const obj2Keys = Object.keys(obj2);
        if (obj1Keys.length !== obj2Keys.length) {
          return true;
        }
        for (const key of obj1Keys) {
          if (hasChanged(obj1[key], obj2[key])) {
            return true;
          }
        }
        return false;
      } else {
        return obj1 !== obj2;
      }
    }
    return false;
  };

  return hasChanged(current, next);
}

export function getIncentiveTypes() {
  return [
    "CASH",
    "LEASE",
    "APR",
    "APR_UNDEF",
    "BALLOON",
    "PROMOTION",
    "CASH_AND_APR",
    "CASH_OR_APR"
  ];
}

export function getIncentiveTypeLabel(
  type: string,
  isFordDirect: boolean
): string {
  if (type === "BALLOON" && isFordDirect) {
    return Locale.getText("FLEX_BUY_APR") as string;
  }
  return Locale.getText(type) as string;
}

export function getIncentiveTypesDropdown(isFordDirect = false) {
  const incentiveTypes = getIncentiveTypes().map(type => ({
    disabled: false,
    label: getIncentiveTypeLabel(type, isFordDirect),
    value: type
  }));
  return incentiveTypes;
}

export function getEmptyIncentive(
  filters: Filters = defaultFilters,
  accountId?: string,
  contentDefaults?: IncentiveContentDefaultsPayload
): IncentivesRecord {
  const defaults = findDefaultsForType("CASH", contentDefaults, "en");

  return {
    globalVehicleTrimIdList: [],
    accountMap: {
      status: "DRAFT",
      distributionListIdList: [],
      dealerStackOption: false
    },
    creatorAccountName: accountId || "",
    creatorAccountId: accountId || "",
    created: Date.now(),
    applicableCondition: filters.condition || "NEW",
    applicableMake: filters.make || "",
    applicableVehicleList: [],
    type: "CASH",
    conditional: false,
    featured: false,
    effectiveDate: -1,
    expirationDate: -1,
    cashOption: -1,
    twentyFourMonthApr: -1,
    thirtySixMonthApr: -1,
    thirtyNineMonthApr: -1,
    fortyTwoMonthApr: -1,
    fortyEightMonthApr: -1,
    sixtyMonthApr: -1,
    seventyTwoMonthApr: -1,
    customTermMonths: -1,
    customApr: -1,
    msrp: -1,
    cashDue: -1,
    purchaseOptionPrice: -1,
    vinList: [],
    applicableYear: -1,
    monthlyPayment: -1,
    mileageAllowance: -1,
    mileOverageCharge: -1,
    content: {
      applicableBodyStyle: "",
      applicableModel: "",
      content: defaults?.content || "",
      disclaimer: defaults?.disclaimer || "",
      incentiveId: "",
      languageCode: "en",
      offerDetails: "",
      shortTitle: "",
      tcdHeadline: "",
      tcdKeywords: "",
      tcdLine1: "",
      tcdLine2: "",
      title: ""
    }
  };
}

export function getTabStates(
  incentives: IncentivesPayload,
  hasAnyUpstreamCompliance: boolean
): IncentivesState[] {
  const incentiveData = incentives.logicalStates;
  if (!hasAnyUpstreamCompliance) {
    return incentiveData.filter(
      state => state.logicalState !== "UNDER_COMPLIANCE_REVIEW"
    );
  }
  return incentiveData;
}

export function onlyUniqueEntries(values: number[]) {
  return values.filter((item, index) => {
    return values.indexOf(item) === index; // only unique entries
  });
}

export function doesArraysHaveCommonString(
  arr1: string[],
  arr2: string[]
): boolean {
  if (!arr1.length || !arr2.length) return false;
  const largerArr = arr1.length >= arr2.length ? arr1 : arr2;
  const smallerArr = arr1.length < arr2.length ? arr1 : arr2;
  const set = new Set(largerArr);
  for (const str of smallerArr) {
    if (set.has(str)) return true;
  }
  return false;
}

export function deepCopyObject(obj) {
  let newObj;
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (obj instanceof Map) {
    newObj = new Map();
    obj.forEach((value, key) => {
      newObj.set(key, deepCopyObject(value));
    });
  } else if ("length" in obj) {
    newObj = [...obj];
    newObj.forEach((child, idx) => {
      if (typeof child === "object") {
        newObj[idx] = deepCopyObject(child);
      }
    });
  } else {
    newObj = { ...obj };
    const keys = Object.keys(newObj);
    keys.forEach((key, idx) => {
      if (typeof newObj[keys[idx]] === "object") {
        newObj[keys[idx]] = deepCopyObject(newObj[keys[idx]]);
      }
    });
  }
  return newObj;
}

export function cloneIncentive(
  incentive: IncentivesRecord,
  accountId: string
): IncentivesRecord {
  const clonedIncentive: IncentivesRecord = {
    ...incentive,
    content: {
      ...incentive.content,
      incentiveId: undefined
    },
    creatorAccountId: accountId,
    clonedFromIncentiveId: incentive.id,
    thirdPartyIncentiveId: undefined,
    accountMap: {
      ...incentive.accountMap,
      status: "DRAFT",
      statusString: "DRAFT",
      incentiveId: undefined
    },
    id: undefined,
    temporalState: "NOT_YET_SCHEDULED"
  };
  return clonedIncentive;
}

export function updateContent(
  incentive: IncentivesRecord,
  field: string,
  value: string | undefined,
  languageCode: string
): IncentivesRecord {
  const updatedLanguageContentMap = {
    ...incentive.languageContentMap,
    [languageCode]: {
      ...incentive?.languageContentMap?.[languageCode],
      [field]: value
    }
  };
  return {
    ...incentive,
    content: { ...incentive.content, [field]: value },
    languageContentMap: updatedLanguageContentMap
  };
}

export function findDefaultsForType(
  incentiveType: string,
  contentDefaults?: IncentiveContentDefaultsPayload,
  languageCode?: string
) {
  if (!Array.isArray(contentDefaults)) {
    return undefined;
  }
  const candidates = contentDefaults.filter(
    content =>
      content.incentiveType === incentiveType &&
      content.languageCode === languageCode
  );
  if (candidates?.length === 1) {
    return candidates[0];
  } else {
    return undefined;
  }
}

export interface TypeContentChangeResult {
  disclaimerChanged: boolean;
  newDisclaimer: string;
  descriptionChanged: boolean;
  newDescription: string;
}

export function typeContentChange(
  incentive: IncentivesRecord,
  fromType: string,
  toType: string,
  contentDefaults: IncentiveContentDefaultsPayload,
  languageCode: string
): TypeContentChangeResult {
  const fromDefaults = findDefaultsForType(
    fromType,
    contentDefaults,
    languageCode
  );
  const toDefaults = findDefaultsForType(toType, contentDefaults, languageCode);

  if (fromDefaults && toDefaults) {
    let descriptionChanged = false;
    let disclaimerChanged = false;
    const newDescription = toDefaults.content || "";
    const newDisclaimer = toDefaults.disclaimer || "";

    const currentDescription = incentive.content?.content?.trim();
    const currentDisclaimer = incentive.content?.disclaimer?.trim();

    if (
      currentDescription === fromDefaults.content?.trim() &&
      currentDescription !== toDefaults.content?.trim()
    ) {
      descriptionChanged = true;
    }
    if (
      currentDisclaimer === fromDefaults.disclaimer?.trim() &&
      currentDisclaimer !== toDefaults.disclaimer?.trim()
    ) {
      disclaimerChanged = true;
    }
    return {
      newDisclaimer,
      newDescription,
      disclaimerChanged,
      descriptionChanged
    };
  }
  return {
    newDisclaimer: "",
    newDescription: "",
    disclaimerChanged: false,
    descriptionChanged: false
  };
}

export function startOfDay(time: number | undefined) {
  const time0 = time ?? new Date().getTime();
  const date = new Date(time0);
  date.setUTCHours(0, 0, 0, 0);
  return date.getTime();
}

// cribbed code from https://ghe.coxautoinc.com/DDC-Inventory/global-vehicle-services/blob/main/service/src/main/java/com/dealer/service/data/incentive/thirdparty/ddc/DDCIncentiveLister.java
export function getIncentiveLogicalState(
  i: IncentivesRecord,
  time: number | undefined
) {
  return getIncentiveLogicalStateInternal(
    i.accountMap.status || "",
    i.effectiveDate,
    i.expirationDate,
    startOfDay(time)
  );
}

export function getUniqueCreatorAccountNames(
  incentiveData: IncentivesPayload
): string[] {
  const creatorAccountNames = new Set<string>();
  incentiveData?.logicalStates.forEach(state => {
    state.incentives.forEach(incentive => {
      if (incentive.creatorAccountName && incentive.creatorAccountName.trim()) {
        creatorAccountNames.add(incentive.creatorAccountName.trim());
      }
    });
  });

  return Array.from(creatorAccountNames).sort();
}

// exported for testing
export function getIncentiveLogicalStateInternal(
  status: string,
  effectiveDate: number,
  expirationDate: number,
  dayStart: number
) {
  const dayMsecs = 24 * 60 * 60 * 1000;
  if (status === "DRAFT") {
    return "DRAFTS";
  }

  if (expirationDate + dayMsecs <= dayStart) {
    return "EXPIRED";
  }

  if (status.startsWith("ACTREQ")) {
    return "ACTION_REQUIRED";
  }
  if (status === "PENDING_COMP") {
    return "UNDER_COMPLIANCE_REVIEW";
  }
  if (status === "REJECTED") {
    return "DECLINED";
  }

  if (status === "PUBLISHED") {
    if (effectiveDate > dayStart) {
      return "PENDING_PUBLICATION";
    } else {
      return "PUBLISHED";
    }
  }

  return "";
}

export const FILTER_ICON =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCI+PHBhdGggc3Ryb2tlPSJub25lIiBkPSJNOSwxOWMtMC42LDAtMS0wLjQtMS0xczAuNC0xLDEtMWg2YzAuNiwwLDEsMC40LDEsMXMtMC40LDEtMSwxSDl6IE02LDEzYy0wLjYsMC0xLTAuNC0xLTFzMC40LTEsMS0xaDEyIGMwLjYsMCwxLDAuNCwxLDFzLTAuNCwxLTEsMUg2eiBNMyw3QzIuNCw3LDIsNi42LDIsNnMwLjQtMSwxLTFoMThjMC42LDAsMSwwLjQsMSwxcy0wLjQsMS0xLDFIM3oiLz48L3N2Zz4K";
